import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Edit3,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  ShieldUser,
  Trash2,
  UserPlus,
  X,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';

/**
 * User Management — super_admin only.
 *
 * Three tabs:
 *  - Users:        list, create, edit (display_name / first_name / last_name /
 *                  department / is_active / login methods), delete, role assign
 *  - Roles:        list, create custom role, edit description, manage role
 *                  permissions, delete (non-system only)
 *  - Permissions:  read-only catalog (mutating at runtime would be reverted by
 *                  the seed loop on next backend boot)
 *
 * Self-protection: every row that matches the current user's `id` has its
 * mutating actions disabled with a "your account" hint. The backend also
 * rejects self-mutations with a 403 — both layers in agreement.
 *
 * Theme: cream background (#FAF7F1) + burgundy primary (#6E3450) — matches
 * the app shell's Smart Insights styling.
 */

interface UserRow {
  user_id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  display_name: string | null;
  department: string | null;
  additional_details: Record<string, unknown> | null;
  office_location: string | null;
  is_active: boolean;
  allowed_login_methods: string[];
  roles: string[];
  creation_method: string | null;
  last_login_at: string | null;
}

interface RoleRow {
  name: string;
  description: string | null;
  is_system: boolean;
  user_count: number;
  permission_count: number;
}

interface PermissionRow {
  permission_id: number;
  name: string;
  module: string | null;
  action: string | null;
  description: string | null;
}

type Tab = 'users' | 'roles' | 'permissions';
type SortDir = 'asc' | 'desc';
interface SortSpec<K extends string = string> {
  column: K;
  direction: SortDir;
}

const ALL_LOGIN_METHODS = ['sso', 'password'] as const;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 25;

type UserSortKey = 'email' | 'name' | 'department' | 'status';
type RoleSortKey = 'name' | 'description' | 'type' | 'users' | 'permissions';
type PermissionSortKey = 'name' | 'module' | 'action' | 'description';

function userDepartment(user: UserRow): string {
  const workbookDepartment = user.additional_details?.department;
  if (typeof workbookDepartment === 'string' && workbookDepartment.trim()) {
    return workbookDepartment.trim();
  }
  return user.department ?? '';
}

export default function UserManagementPage(): React.ReactElement {
  const { user: currentUser } = useAuth();
  const selfId = currentUser?.id ?? null;

  const [tab, setTab] = useState<Tab>('users');
  const [users, setUsers] = useState<UserRow[]>([]);
  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [permissions, setPermissions] = useState<PermissionRow[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchUsers, setSearchUsers] = useState('');
  const [searchRoles, setSearchRoles] = useState('');
  const [searchPermissions, setSearchPermissions] = useState('');

  // Per-tab sort state. Defaults sort by the first natural column ascending.
  const [userSort, setUserSort] = useState<SortSpec<UserSortKey>>({ column: 'email', direction: 'asc' });
  const [roleSort, setRoleSort] = useState<SortSpec<RoleSortKey>>({ column: 'name', direction: 'asc' });
  const [permSort, setPermSort] = useState<SortSpec<PermissionSortKey>>({ column: 'name', direction: 'asc' });

  // Per-tab pagination state. Page is 1-indexed for display; reset to 1
  // any time the search query or page-size for that tab changes.
  const [userPage, setUserPage] = useState(1);
  const [userPageSize, setUserPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [rolePage, setRolePage] = useState(1);
  const [rolePageSize, setRolePageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [permPage, setPermPage] = useState(1);
  const [permPageSize, setPermPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  // Reset to page 1 when the underlying filtered set could shrink.
  useEffect(() => setUserPage(1), [searchUsers, userPageSize, userSort]);
  useEffect(() => setRolePage(1), [searchRoles, rolePageSize, roleSort]);
  useEffect(() => setPermPage(1), [searchPermissions, permPageSize, permSort]);

  // Modals
  const [editingUser, setEditingUser] = useState<UserRow | null>(null);
  const [creatingUser, setCreatingUser] = useState(false);
  const [editingRole, setEditingRole] = useState<RoleRow | null>(null);
  const [creatingRole, setCreatingRole] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const refreshToken = Date.now().toString();
      const refreshConfig = {
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
        params: {
          _refresh: refreshToken,
        },
      };
      const [usersRes, rolesRes, permsRes] = await Promise.all([
        api.get<UserRow[]>('/api/v1/admin/users', refreshConfig),
        api.get<RoleRow[]>('/api/v1/admin/roles', refreshConfig),
        api.get<PermissionRow[]>('/api/v1/admin/permissions', refreshConfig),
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
      setPermissions(permsRes.data);
    } catch (err) {
      setError(humanError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const sortedUsers = useMemo(() => {
    const needle = searchUsers.trim().toLowerCase();
    const filtered = !needle
      ? users
      : users.filter((u) =>
          [u.email, u.first_name, u.last_name, u.display_name, userDepartment(u), u.office_location, ...u.roles]
            .filter(Boolean)
            .join(' ')
            .toLowerCase()
            .includes(needle),
        );
    const out = [...filtered];
    out.sort((a, b) => {
      const dir = userSort.direction === 'asc' ? 1 : -1;
      switch (userSort.column) {
        case 'email':
          return cmpStr(a.email, b.email) * dir;
        case 'name':
          return (
            cmpStr(
              userDisplay(a).toLowerCase(),
              userDisplay(b).toLowerCase(),
            ) * dir
          );
        case 'department':
          return cmpStr(userDepartment(a), userDepartment(b)) * dir;
        case 'status':
          return cmpBool(a.is_active, b.is_active) * dir;
        default:
          return 0;
      }
    });
    return out;
  }, [users, searchUsers, userSort]);

  const sortedRoles = useMemo(() => {
    const needle = searchRoles.trim().toLowerCase();
    const filtered = !needle
      ? roles
      : roles.filter((r) =>
          `${r.name} ${r.description ?? ''}`.toLowerCase().includes(needle),
        );
    const out = [...filtered];
    out.sort((a, b) => {
      const dir = roleSort.direction === 'asc' ? 1 : -1;
      switch (roleSort.column) {
        case 'name':
          return cmpStr(a.name, b.name) * dir;
        case 'description':
          return cmpStr(a.description ?? '', b.description ?? '') * dir;
        case 'type':
          return cmpBool(a.is_system, b.is_system) * dir;
        case 'users':
          return (a.user_count - b.user_count) * dir;
        case 'permissions':
          return (a.permission_count - b.permission_count) * dir;
        default:
          return 0;
      }
    });
    return out;
  }, [roles, searchRoles, roleSort]);

  const sortedPermissions = useMemo(() => {
    const needle = searchPermissions.trim().toLowerCase();
    const filtered = !needle
      ? permissions
      : permissions.filter((p) =>
          `${p.name} ${p.module ?? ''} ${p.action ?? ''} ${p.description ?? ''}`
            .toLowerCase()
            .includes(needle),
        );
    const out = [...filtered];
    out.sort((a, b) => {
      const dir = permSort.direction === 'asc' ? 1 : -1;
      switch (permSort.column) {
        case 'name':
          return cmpStr(a.name, b.name) * dir;
        case 'module':
          return cmpStr(a.module ?? '', b.module ?? '') * dir;
        case 'action':
          return cmpStr(a.action ?? '', b.action ?? '') * dir;
        case 'description':
          return cmpStr(a.description ?? '', b.description ?? '') * dir;
        default:
          return 0;
      }
    });
    return out;
  }, [permissions, searchPermissions, permSort]);

  const pagedUsers = useMemo(
    () => sortedUsers.slice((userPage - 1) * userPageSize, userPage * userPageSize),
    [sortedUsers, userPage, userPageSize],
  );
  const pagedRoles = useMemo(
    () => sortedRoles.slice((rolePage - 1) * rolePageSize, rolePage * rolePageSize),
    [sortedRoles, rolePage, rolePageSize],
  );
  const pagedPermissions = useMemo(
    () => sortedPermissions.slice((permPage - 1) * permPageSize, permPage * permPageSize),
    [sortedPermissions, permPage, permPageSize],
  );

  const renderRefreshButton = () => (
    <button
      onClick={refresh}
      disabled={loading}
      className="inline-flex h-9 items-center gap-2 rounded-lg border border-[#6E3450] bg-transparent px-3 py-0 text-sm font-medium text-[#6E3450] hover:bg-[#F5E9EE] disabled:opacity-50"
    >
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
      Refresh
    </button>
  );

  return (
    <div className="relative flex h-full w-full flex-col overflow-auto bg-[#FAF7F1] text-[#1F1A20] p-6">
      <div className="mx-auto w-full max-w-7xl space-y-6">
        {/* Header */}
        <div>
          <div>
            <h1 className="font-display text-2xl font-semibold text-[#1F1A20] flex items-center gap-2">
              <ShieldUser className="w-6 h-6 text-[#6E3450]" />
              User Management
            </h1>
            <p className="text-sm text-[#7B6B72] mt-1">
              {/* Manage users, roles, and the permission catalog. The super-admin role short-circuits
              every permission check — this surface is restricted to super-admins. */}
              Manage users, roles, and permission configurations. <br />Access to this section is restricted to super-admins, as the super-admin role bypasses all permission validations.
            </p>
          </div>
        </div>

        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

        {/* Tabs */}
        <div className="border-b border-[#EDE6E1] flex items-center gap-6">
          <TabButton label="Users" count={users.length} active={tab === 'users'} onClick={() => setTab('users')} />
          <TabButton label="Roles" count={roles.length} active={tab === 'roles'} onClick={() => setTab('roles')} />
          <TabButton
            label="Permissions"
            count={permissions.length}
            active={tab === 'permissions'}
            onClick={() => setTab('permissions')}
          />
        </div>

        {/* Users tab */}
        {tab === 'users' && (
          <section className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <SearchBox value={searchUsers} onChange={setSearchUsers} placeholder="Search users…" />
              <div className="flex items-center gap-2">
                {renderRefreshButton()}
                <button
                  onClick={() => setCreatingUser(true)}
                  className="inline-flex h-9 items-center gap-2 rounded-lg bg-[#6E3450] px-4 py-0 text-sm font-medium text-white shadow-sm hover:bg-[#5A2A41]"
                >
                  <UserPlus className="w-4 h-4" /> Add User
                </button>
              </div>
            </div>
            <div className="rounded-xl border border-[#EDE6E1] bg-white shadow-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#FAF7F1] text-[#7B6B72] text-xs uppercase tracking-wide">
                  <tr>
                    <SortableTh<UserSortKey> sort={userSort} setSort={setUserSort} column="email">Email</SortableTh>
                    <SortableTh<UserSortKey> sort={userSort} setSort={setUserSort} column="name">Name</SortableTh>
                    <SortableTh<UserSortKey> sort={userSort} setSort={setUserSort} column="department">Department</SortableTh>
                    <th className="px-4 py-3 text-left font-semibold">Office Location</th>
                    <th className="px-4 py-3 text-left font-semibold">Roles</th>
                    <th className="px-4 py-3 text-left font-semibold">Login</th>
                    <SortableTh<UserSortKey> sort={userSort} setSort={setUserSort} column="status">Status</SortableTh>
                    <th className="px-4 py-3 text-right font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1ECE6]">
                  {pagedUsers.length === 0 && !loading && (
                    <tr>
                      <td colSpan={8} className="px-4 py-12 text-center text-[#7B6B72]">
                        {users.length === 0 ? 'No users yet.' : 'No matches.'}
                      </td>
                    </tr>
                  )}
                  {pagedUsers.map((u) => {
                    const isSelf = u.user_id === selfId;
                    return (
                      <tr key={u.user_id} className="hover:bg-[#FAF7F1]/60">
                        <td className="px-4 py-3 font-mono text-xs text-[#1F1A20]">
                          {u.email}
                          {isSelf && (
                            <span className="ml-2 rounded bg-[#F5E9EE] text-[#5A2A41] text-[10px] uppercase tracking-wide px-1.5 py-0.5">
                              You
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {u.display_name || `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || (
                            <span className="text-[#A8989F] italic">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-[#7B6B72]">{userDepartment(u) || '—'}</td>
                        <td className="px-4 py-3 text-[#7B6B72]">{u.office_location || '—'}</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {u.roles.length === 0 && <span className="text-[#A8989F] italic text-xs">none</span>}
                            {u.roles.map((r) => (
                              <span
                                key={r}
                                className="rounded bg-[#F5E9EE] text-[#5A2A41] text-[11px] px-1.5 py-0.5"
                              >
                                {r}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-[#7B6B72] font-mono">
                          {u.allowed_login_methods.join(', ')}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${
                              u.is_active
                                ? 'bg-emerald-50 text-emerald-700'
                                : 'bg-neutral-100 text-neutral-500'
                            }`}
                          >
                            {u.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <IconButton
                              title={isSelf ? 'You cannot edit your own profile.' : 'Edit user'}
                              disabled={isSelf}
                              onClick={() => setEditingUser(u)}
                            >
                              <Edit3 className="w-4 h-4" />
                            </IconButton>
                            {/* <IconButton
                                variant="danger"
                                title={isSelf ? 'You cannot delete your own profile.' : 'Delete user'}
                                disabled={isSelf}
                                onClick={() => handleDeleteUser(u, refresh, setError)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </IconButton> */}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <Pagination
                total={sortedUsers.length}
                page={userPage}
                pageSize={userPageSize}
                onPageChange={setUserPage}
                onPageSizeChange={setUserPageSize}
              />
            </div>
          </section>
        )}

        {/* Roles tab */}
        {tab === 'roles' && (
          <section className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <SearchBox value={searchRoles} onChange={setSearchRoles} placeholder="Search roles…" />
              <div className="flex items-center gap-2">
                {renderRefreshButton()}
                {/* <button
                    onClick={() => setCreatingRole(true)}
                    className="inline-flex h-9 items-center gap-2 rounded-lg bg-[#6E3450] px-4 py-0 text-sm font-medium text-white shadow-sm hover:bg-[#5A2A41]"
                  >
                    <Plus className="w-4 h-4" /> Add Role
                  </button> */}
              </div>
            </div>
            <div className="rounded-xl border border-[#EDE6E1] bg-white shadow-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#FAF7F1] text-[#7B6B72] text-xs uppercase tracking-wide">
                  <tr>
                    <SortableTh<RoleSortKey> sort={roleSort} setSort={setRoleSort} column="name">Name</SortableTh>
                    <SortableTh<RoleSortKey> sort={roleSort} setSort={setRoleSort} column="description">Description</SortableTh>
                    <SortableTh<RoleSortKey> sort={roleSort} setSort={setRoleSort} column="type">Type</SortableTh>
                    <SortableTh<RoleSortKey> sort={roleSort} setSort={setRoleSort} column="users">Users</SortableTh>
                    <SortableTh<RoleSortKey> sort={roleSort} setSort={setRoleSort} column="permissions">Permissions</SortableTh>
                    <th className="px-4 py-3 text-right font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1ECE6]">
                  {pagedRoles.length === 0 && !loading && (
                    <tr>
                      <td colSpan={6} className="px-4 py-12 text-center text-[#7B6B72]">
                        No roles match.
                      </td>
                    </tr>
                  )}
                  {pagedRoles.map((r) => (
                    <tr key={r.name} className="hover:bg-[#FAF7F1]/60">
                      <td className="px-4 py-3 font-mono text-xs text-[#1F1A20]">{r.name}</td>
                      <td className="px-4 py-3 text-[#7B6B72]">{r.description || '—'}</td>
                      <td className="px-4 py-3">
                        {r.is_system ? (
                          <span className="rounded bg-neutral-100 text-neutral-700 text-[11px] px-1.5 py-0.5">
                            system
                          </span>
                        ) : (
                          <span className="rounded bg-[#F5E9EE] text-[#5A2A41] text-[11px] px-1.5 py-0.5">
                            custom
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[#1F1A20]">{r.user_count}</td>
                      <td className="px-4 py-3 text-[#1F1A20]">{r.permission_count}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <IconButton title="Edit description / permissions" onClick={() => setEditingRole(r)}>
                            <Edit3 className="w-4 h-4" />
                          </IconButton>
                          {/* <IconButton
                              variant="danger"
                              title={r.is_system ? 'System roles cannot be deleted.' : 'Delete role'}
                              disabled={r.is_system}
                              onClick={() => handleDeleteRole(r, refresh, setError)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </IconButton> */}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination
                total={sortedRoles.length}
                page={rolePage}
                pageSize={rolePageSize}
                onPageChange={setRolePage}
                onPageSizeChange={setRolePageSize}
              />
            </div>
          </section>
        )}

        {/* Permissions tab */}
        {tab === 'permissions' && (
          <section className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <SearchBox
                value={searchPermissions}
                onChange={setSearchPermissions}
                placeholder="Search permissions…"
              />
              {renderRefreshButton()}
            </div>
            <div className="rounded-xl border border-[#EDE6E1] bg-white shadow-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#FAF7F1] text-[#7B6B72] text-xs uppercase tracking-wide">
                  <tr>
                    <SortableTh<PermissionSortKey> sort={permSort} setSort={setPermSort} column="name">Name</SortableTh>
                    <SortableTh<PermissionSortKey> sort={permSort} setSort={setPermSort} column="module">Module</SortableTh>
                    <SortableTh<PermissionSortKey> sort={permSort} setSort={setPermSort} column="action">Action</SortableTh>
                    <SortableTh<PermissionSortKey> sort={permSort} setSort={setPermSort} column="description">Description</SortableTh>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1ECE6]">
                  {pagedPermissions.length === 0 && !loading && (
                    <tr>
                      <td colSpan={4} className="px-4 py-12 text-center text-[#7B6B72]">
                        No permissions match.
                      </td>
                    </tr>
                  )}
                  {pagedPermissions.map((p) => (
                    <tr key={p.permission_id} className="hover:bg-[#FAF7F1]/60">
                      <td className="px-4 py-3 font-mono text-xs text-[#1F1A20]">{p.name}</td>
                      <td className="px-4 py-3 text-[#7B6B72]">{p.module || '—'}</td>
                      <td className="px-4 py-3 text-[#7B6B72]">{p.action || '—'}</td>
                      <td className="px-4 py-3 text-[#7B6B72]">{p.description || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination
                total={sortedPermissions.length}
                page={permPage}
                pageSize={permPageSize}
                onPageChange={setPermPage}
                onPageSizeChange={setPermPageSize}
              />
            </div>
          </section>
        )}
      </div>

      {/* Modals */}
      {editingUser && (
        <UserEditModal
          user={editingUser}
          roles={roles}
          isSelf={editingUser.user_id === selfId}
          onClose={() => setEditingUser(null)}
          onSaved={async () => {
            setEditingUser(null);
            await refresh();
          }}
        />
      )}
      {creatingUser && (
        <UserCreateModal
          onClose={() => setCreatingUser(false)}
          onSaved={async () => {
            setCreatingUser(false);
            await refresh();
          }}
        />
      )}
      {editingRole && (
        <RoleEditModal
          role={editingRole}
          permissions={permissions}
          onClose={() => setEditingRole(null)}
          onSaved={async () => {
            setEditingRole(null);
            await refresh();
          }}
        />
      )}
      {creatingRole && (
        <RoleCreateModal
          onClose={() => setCreatingRole(false)}
          onSaved={async () => {
            setCreatingRole(false);
            await refresh();
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Mutations
// ─────────────────────────────────────────────────────────────────────────

async function handleDeleteUser(
  u: UserRow,
  refresh: () => Promise<void>,
  setError: (s: string | null) => void,
): Promise<void> {
  if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
  try {
    await api.delete(`/api/v1/admin/users/${u.user_id}`);
    await refresh();
  } catch (err) {
    setError(humanError(err));
  }
}

async function handleDeleteRole(
  r: RoleRow,
  refresh: () => Promise<void>,
  setError: (s: string | null) => void,
): Promise<void> {
  if (!window.confirm(`Delete role "${r.name}"? This cannot be undone.`)) return;
  try {
    await api.delete(`/api/v1/admin/roles/${r.name}`);
    await refresh();
  } catch (err) {
    setError(humanError(err));
  }
}

// ─────────────────────────────────────────────────────────────────────────
// User edit modal — editable fields + role assignment + login methods
// ─────────────────────────────────────────────────────────────────────────

interface UserEditModalProps {
  user: UserRow;
  roles: RoleRow[];
  isSelf: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
}

function UserEditModal({ user, roles, isSelf, onClose, onSaved }: UserEditModalProps): React.ReactElement {
  const [firstName, setFirstName] = useState(user.first_name ?? '');
  const [lastName, setLastName] = useState(user.last_name ?? '');
  const [displayName, setDisplayName] = useState(user.display_name ?? '');
  const [department, setDepartment] = useState(user.department ?? '');
  const [isActive, setIsActive] = useState(user.is_active);
  const [methods, setMethods] = useState<string[]>(user.allowed_login_methods);
  const [assignedRoles, setAssignedRoles] = useState<string[]>(user.roles);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Edits to your own row are blocked at the modal level too — even though
  // the backend will 403, refusing client-side avoids partial state on the
  // user list when one mutation succeeds and another doesn't.
  const disabled = isSelf || busy;

  const save = async () => {
    if (isSelf) return;
    setBusy(true);
    setError(null);
    try {
      // 1. PATCH editable fields.
      await api.patch(`/api/v1/admin/users/${user.user_id}`, {
        first_name: firstName || null,
        last_name: lastName || null,
        display_name: displayName || null,
        department: department || null,
        is_active: isActive,
      });

      // 2. PUT login methods.
      await api.put(`/api/v1/admin/users/${user.user_id}/login-methods`, { methods });

      // 3. Reconcile role membership: assign each role in the new set
      // that isn't already on the user, and revoke each existing role
      // that's no longer selected.
      const before = new Set(user.roles);
      const after = new Set(assignedRoles);
      for (const r of assignedRoles) {
        if (!before.has(r)) {
          await api.post(`/api/v1/admin/users/${user.user_id}/roles`, { role_name: r });
        }
      }
      for (const r of user.roles) {
        if (!after.has(r)) {
          await api.delete(`/api/v1/admin/users/${user.user_id}/roles/${r}`);
        }
      }
      await onSaved();
    } catch (err) {
      setError(humanError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell
      title={`Edit ${user.email}${isSelf ? ' (your account — read-only)' : ''}`}
      onClose={onClose}
      size="lg"
    >
      <div className="space-y-3">
        <ReadOnlyField label="Email" value={user.email} />
        <ReadOnlyField label="User ID" value={user.user_id} mono />
        <ReadOnlyField
          label="Created via"
          value={user.creation_method || '—'}
        />
        <ReadOnlyField
          label="Last login"
          value={user.last_login_at ? new Date(user.last_login_at).toLocaleString() : '—'}
        />
        <div className="grid grid-cols-2 gap-3">
          <TextField label="First name" value={firstName} onChange={setFirstName} disabled={disabled} />
          <TextField label="Last name" value={lastName} onChange={setLastName} disabled={disabled} />
          <TextField label="Display name" value={displayName} onChange={setDisplayName} disabled={disabled} />
          <TextField label="Department" value={department} onChange={setDepartment} disabled={disabled} />
        </div>
        <ToggleField
          label="Account status"
          checked={isActive}
          onChange={setIsActive}
          disabled={disabled}
          onText="Active — the user can log in and use the app."
          offText="Inactive — the user cannot log in until reactivated."
        />
        <MultiSelectDropdown
          label="Login methods"
          options={ALL_LOGIN_METHODS.map((m) => ({ value: m, label: m.toUpperCase() }))}
          selected={methods}
          onChange={setMethods}
          disabled={disabled}
          placeholder="Select login methods"
        />
        <MultiSelectDropdown
          label="Roles"
          options={roles.map((r) => ({ value: r.name, label: r.name }))}
          selected={assignedRoles}
          onChange={setAssignedRoles}
          disabled={disabled}
          searchable
          placeholder="Select roles"
        />
        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      </div>
      <ModalFooter onClose={onClose} onSave={save} busy={busy} disabled={isSelf} />
    </ModalShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// User create modal
// ─────────────────────────────────────────────────────────────────────────

interface UserCreateModalProps {
  onClose: () => void;
  onSaved: () => Promise<void>;
}

function UserCreateModal({ onClose, onSaved }: UserCreateModalProps): React.ReactElement {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [department, setDepartment] = useState('');
  const [methods, setMethods] = useState<string[]>(['sso']);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!email.trim()) {
      setError('Email is required.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/admin/users', {
        email: email.trim(),
        first_name: firstName.trim() || null,
        last_name: lastName.trim() || null,
        department: department.trim() || null,
        is_active: true,
        allowed_login_methods: methods,
      });
      await onSaved();
    } catch (err) {
      setError(humanError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title="Add User" onClose={onClose} size="lg">
      <div className="space-y-3">
        <TextField label="Email" value={email} onChange={setEmail} required />
        <div className="grid grid-cols-2 gap-3">
          <TextField label="First name" value={firstName} onChange={setFirstName} />
          <TextField label="Last name" value={lastName} onChange={setLastName} />
        </div>
        <TextField label="Department" value={department} onChange={setDepartment} />
        <MultiSelectDropdown
          label="Login methods"
          options={ALL_LOGIN_METHODS.map((m) => ({ value: m, label: m.toUpperCase() }))}
          selected={methods}
          onChange={setMethods}
          placeholder="Select login methods"
        />
        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
        <p className="text-xs text-[#7B6B72] italic">
          Roles can be assigned after creation from the user's edit dialog.
        </p>
      </div>
      <ModalFooter onClose={onClose} onSave={save} busy={busy} />
    </ModalShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Role edit modal — description + permission grants (single-step apply)
// ─────────────────────────────────────────────────────────────────────────

interface RoleEditModalProps {
  role: RoleRow;
  permissions: PermissionRow[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}

function RoleEditModal({ role, permissions, onClose, onSaved }: RoleEditModalProps): React.ReactElement {
  const [description, setDescription] = useState(role.description ?? '');
  const [grantedNames, setGrantedNames] = useState<Set<string> | null>(null);
  const [busy, setBusy] = useState(false);
  const [loadingGranted, setLoadingGranted] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get<PermissionRow[]>(`/api/v1/admin/roles/${role.name}/permissions`)
      .then((res) => {
        if (cancelled) return;
        setGrantedNames(new Set(res.data.map((p) => p.name)));
      })
      .catch((err) => {
        if (cancelled) return;
        setError(humanError(err));
      })
      .finally(() => !cancelled && setLoadingGranted(false));
    return () => {
      cancelled = true;
    };
  }, [role.name]);

  const save = async () => {
    if (!grantedNames) return;
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/api/v1/admin/roles/${role.name}`, { description: description || null });
      // Reconcile permissions.
      const beforeRes = await api.get<PermissionRow[]>(`/api/v1/admin/roles/${role.name}/permissions`);
      const before = new Set(beforeRes.data.map((p) => p.name));
      for (const name of grantedNames) {
        if (!before.has(name)) {
          await api.post(`/api/v1/admin/roles/${role.name}/permissions`, { permission_name: name });
        }
      }
      for (const name of before) {
        if (!grantedNames.has(name)) {
          await api.delete(`/api/v1/admin/roles/${role.name}/permissions/${name}`);
        }
      }
      await onSaved();
    } catch (err) {
      setError(humanError(err));
    } finally {
      setBusy(false);
    }
  };

  const togglePermission = (name: string, on: boolean) => {
    setGrantedNames((prev) => {
      const next = new Set(prev);
      if (on) next.add(name);
      else next.delete(name);
      return next;
    });
  };

  return (
    <ModalShell title={`Edit role: ${role.name}`} onClose={onClose}>
      <div className="space-y-3">
        <ReadOnlyField label="Name" value={role.name} mono />
        <ReadOnlyField label="Type" value={role.is_system ? 'system' : 'custom'} />
        <TextField label="Description" value={description} onChange={setDescription} />
        <div>
          <label className="text-xs font-semibold text-[#7B6B72]">Permissions</label>
          {loadingGranted ? (
            <div className="text-sm text-[#7B6B72] italic">Loading…</div>
          ) : (
            <div className="mt-2 max-h-72 overflow-y-auto rounded-md border border-[#EDE6E1] divide-y divide-[#F1ECE6]">
              {permissions.map((p) => (
                <label
                  key={p.permission_id}
                  className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-[#FAF7F1]"
                >
                  <input
                    type="checkbox"
                    checked={grantedNames?.has(p.name) ?? false}
                    onChange={(e) => togglePermission(p.name, e.target.checked)}
                  />
                  <span className="font-mono text-[12px] text-[#1F1A20]">{p.name}</span>
                  {p.description && (
                    <span className="text-[#7B6B72] text-xs ml-auto">{p.description}</span>
                  )}
                </label>
              ))}
            </div>
          )}
        </div>
        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      </div>
      <ModalFooter onClose={onClose} onSave={save} busy={busy} disabled={loadingGranted} />
    </ModalShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Role create modal
// ─────────────────────────────────────────────────────────────────────────

interface RoleCreateModalProps {
  onClose: () => void;
  onSaved: () => Promise<void>;
}

function RoleCreateModal({ onClose, onSaved }: RoleCreateModalProps): React.ReactElement {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!/^[a-z][a-z0-9_]*$/.test(name)) {
      setError('Role name must be lowercase letters, digits, and underscores; starts with a letter.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/admin/roles', {
        name: name.trim(),
        description: description.trim() || null,
      });
      await onSaved();
    } catch (err) {
      setError(humanError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title="Add Role" onClose={onClose}>
      <div className="space-y-3">
        <TextField
          label="Name (lowercase, snake_case)"
          value={name}
          onChange={setName}
          required
        />
        <TextField label="Description" value={description} onChange={setDescription} />
        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
        <p className="text-xs text-[#7B6B72] italic">
          New roles are non-system. Permissions can be granted from the role's edit dialog after creation.
        </p>
      </div>
      <ModalFooter onClose={onClose} onSave={save} busy={busy} />
    </ModalShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Reusable bits
// ─────────────────────────────────────────────────────────────────────────

function TabButton({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}): React.ReactElement {
  return (
    <button
      onClick={onClick}
      className={`pb-3 -mb-px border-b-2 text-sm font-medium transition-colors ${
        active ? 'border-[#6E3450] text-[#6E3450]' : 'border-transparent text-[#7B6B72] hover:text-[#1F1A20]'
      }`}
    >
      {label}
      {active && (
        <span className="ml-2 rounded-full bg-[#F5E9EE] px-1.5 py-0.5 text-[11px] text-[#5A2A41]">
          {count}
        </span>
      )}
    </button>
  );
}

function SearchBox({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}): React.ReactElement {
  return (
    <div className="relative flex-1 max-w-md">
      <Search className="absolute left-3 top-2.5 w-4 h-4 text-[#A8989F]" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-md border border-[#EDE6E1] bg-white pl-9 pr-3 py-2 text-sm focus:border-[#6E3450] focus:outline-none"
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Sort + pagination helpers
// ─────────────────────────────────────────────────────────────────────────

function cmpStr(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: 'base' });
}

function cmpBool(a: boolean, b: boolean): number {
  // True sorts after false in ascending order so toggling 'asc'→'desc' on
  // the Status column moves "Active" to the top — the more useful default.
  return a === b ? 0 : a ? 1 : -1;
}

function userDisplay(u: UserRow): string {
  return (
    u.display_name?.trim() ||
    `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() ||
    u.email
  );
}

function SortableTh<K extends string>({
  column,
  sort,
  setSort,
  children,
  align = 'left',
}: {
  column: K;
  sort: SortSpec<K>;
  setSort: (next: SortSpec<K>) => void;
  children: React.ReactNode;
  align?: 'left' | 'right';
}): React.ReactElement {
  const active = sort.column === column;
  const onClick = () => {
    if (active) {
      setSort({ column, direction: sort.direction === 'asc' ? 'desc' : 'asc' });
    } else {
      setSort({ column, direction: 'asc' });
    }
  };
  return (
    <th className={`px-4 py-3 ${align === 'right' ? 'text-right' : 'text-left'} font-semibold`}>
      <button
        type="button"
        onClick={onClick}
        className={`inline-flex items-center gap-1 select-none ${
          active ? 'text-[#5A2A41]' : 'text-[#7B6B72]'
        } hover:text-[#5A2A41]`}
        aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        {children}
        {active ? (
          sort.direction === 'asc' ? (
            <ChevronUp className="w-3 h-3" />
          ) : (
            <ChevronDown className="w-3 h-3" />
          )
        ) : (
          <ChevronUp className="w-3 h-3 opacity-30" />
        )}
      </button>
    </th>
  );
}

function Pagination({
  total,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
}: {
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}): React.ReactElement | null {
  if (total === 0) return null;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize + 1;
  const end = Math.min(safePage * pageSize, total);

  return (
    <div className="flex items-center justify-between gap-3 border-t border-[#EDE6E1] bg-[#FAF7F1] px-4 py-2 text-xs text-[#7B6B72]">
      <div className="flex items-center gap-2">
        <span>
          {start}–{end} of {total}
        </span>
      </div>
      <div className="flex items-center gap-3">
        <span>Rows per page</span>
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          className="rounded-md border border-[#EDE6E1] bg-white px-2 py-1 text-xs text-[#1F1A20] focus:border-[#6E3450] focus:outline-none"
        >
          {PAGE_SIZE_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onPageChange(safePage - 1)}
            disabled={safePage <= 1}
            className="inline-flex items-center justify-center rounded-md border border-[#EDE6E1] bg-white p-1 text-[#7B6B72] hover:text-[#1F1A20] disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Previous page"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="px-2 text-[#1F1A20] font-medium">
            {safePage} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => onPageChange(safePage + 1)}
            disabled={safePage >= totalPages}
            className="inline-flex items-center justify-center rounded-md border border-[#EDE6E1] bg-white p-1 text-[#7B6B72] hover:text-[#1F1A20] disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Next page"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function IconButton({
  children,
  onClick,
  title,
  disabled,
  variant = 'default',
}: {
  children: React.ReactNode;
  onClick?: () => void;
  title?: string;
  disabled?: boolean;
  variant?: 'default' | 'danger';
}): React.ReactElement {
  const base =
    'inline-flex items-center justify-center rounded-md p-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed';
  const tone =
    variant === 'danger'
      ? 'text-red-600 hover:bg-red-50 hover:text-red-700'
      : 'text-[#7B6B72] hover:bg-[#FAF7F1] hover:text-[#1F1A20]';
  return (
    <button onClick={onClick} title={title} disabled={disabled} className={`${base} ${tone}`}>
      {children}
    </button>
  );
}

function ModalShell({
  title,
  onClose,
  children,
  size = 'md',
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  size?: 'md' | 'lg';
}): React.ReactElement {
  const widthClass = size === 'lg' ? 'max-w-2xl' : 'max-w-lg';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className={`w-full ${widthClass} rounded-xl bg-white shadow-xl`}>
        <div className="flex items-center justify-between border-b border-[#EDE6E1] px-5 py-3">
          <h3 className="text-base font-semibold text-[#1F1A20]">{title}</h3>
          <button onClick={onClose} className="rounded p-1 text-[#7B6B72] hover:bg-[#FAF7F1]">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 max-h-[75vh] overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

function ModalFooter({
  onClose,
  onSave,
  busy,
  disabled,
}: {
  onClose: () => void;
  onSave: () => void;
  busy: boolean;
  disabled?: boolean;
}): React.ReactElement {
  return (
    <div className="flex items-center justify-end gap-2 border-t border-[#EDE6E1] px-5 py-3 bg-[#FAF7F1] rounded-b-xl">
      <button
        onClick={onClose}
        className="rounded-md border border-[#EDE6E1] bg-white px-3 py-1.5 text-sm font-medium text-[#1F1A20] hover:bg-[#FAF7F1]"
      >
        Cancel
      </button>
      <button
        onClick={onSave}
        disabled={busy || disabled}
        className="inline-flex items-center gap-2 rounded-md bg-[#6E3450] px-4 py-1.5 text-sm font-medium text-white hover:bg-[#5A2A41] disabled:opacity-50"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
      </button>
    </div>
  );
}

function TextField({
  label,
  value,
  onChange,
  disabled,
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  required?: boolean;
}): React.ReactElement {
  return (
    <div>
      <label className="text-xs font-semibold text-[#7B6B72]">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="mt-1 w-full rounded-md border border-[#EDE6E1] bg-white px-3 py-2 text-sm focus:border-[#6E3450] focus:outline-none disabled:bg-[#FAF7F1] disabled:text-[#7B6B72]"
      />
    </div>
  );
}

function ReadOnlyField({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): React.ReactElement {
  return (
    <div>
      <label className="text-xs font-semibold text-[#7B6B72]">{label}</label>
      <div
        className={`mt-1 w-full rounded-md border border-[#EDE6E1] bg-[#FAF7F1] px-3 py-2 text-sm text-[#7B6B72] ${
          mono ? 'font-mono text-xs' : ''
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function ToggleField({
  label,
  checked,
  onChange,
  onText,
  offText,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  /** Status message rendered when ``checked`` is true. */
  onText: string;
  /** Status message rendered when ``checked`` is false. */
  offText: string;
  disabled?: boolean;
}): React.ReactElement {
  // Click target is intentionally limited to the switch button itself —
  // the label and status text are plain spans so a misclick on the
  // surrounding text does not toggle the value.
  return (
    <div className="space-y-1">
      <div className="text-xs font-semibold text-[#7B6B72]">{label}</div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          role="switch"
          aria-checked={checked}
          aria-label={label}
          onClick={() => !disabled && onChange(!checked)}
          disabled={disabled}
          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#6E3450]/40 ${
            checked ? 'bg-[#6E3450]' : 'bg-[#D8CDC4]'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
              checked ? 'translate-x-[1.375rem]' : 'translate-x-0.5'
            }`}
          />
        </button>
        <span
          className={`text-sm ${checked ? 'text-[#1F1A20]' : 'text-[#7B6B72]'}`}
          aria-live="polite"
        >
          {checked ? onText : offText}
        </span>
      </div>
    </div>
  );
}

interface MultiSelectOption {
  value: string;
  label: string;
}

function MultiSelectDropdown({
  label,
  options,
  selected,
  onChange,
  placeholder,
  searchable,
  disabled,
}: {
  label: string;
  options: MultiSelectOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  searchable?: boolean;
  disabled?: boolean;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click. Mounted only while ``open`` so we don't pay
  // the global listener cost on every dropdown the page renders.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggle = (value: string) => {
    if (selected.includes(value)) onChange(selected.filter((v) => v !== value));
    else onChange([...selected, value]);
  };

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((o) => o.label.toLowerCase().includes(needle));
  }, [options, search]);

  const selectedLabels = options
    .filter((o) => selected.includes(o.value))
    .map((o) => o.label);

  return (
    <div ref={wrapperRef} className="relative">
      <label className="text-xs font-semibold text-[#7B6B72]">{label}</label>
      <button
        type="button"
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        className={`mt-1 w-full flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm text-left transition-colors ${
          disabled
            ? 'border-[#EDE6E1] bg-[#FAF7F1] text-[#A8989F] cursor-not-allowed'
            : open
              ? 'border-[#6E3450] bg-white'
              : 'border-[#EDE6E1] bg-white hover:border-[#6E3450]/60'
        }`}
      >
        <div className="flex flex-wrap gap-1 min-h-[1.25rem] flex-1">
          {selectedLabels.length === 0 ? (
            <span className="text-[#A8989F]">{placeholder || 'Select…'}</span>
          ) : (
            selectedLabels.map((l, i) => (
              <span
                key={`${l}-${i}`}
                className="inline-flex items-center rounded bg-[#F5E9EE] text-[#5A2A41] text-[11px] px-1.5 py-0.5"
              >
                {l}
              </span>
            ))
          )}
        </div>
        <ChevronDown
          className={`w-4 h-4 text-[#7B6B72] transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && !disabled && (
        <div className="absolute left-0 right-0 z-20 mt-1 rounded-md border border-[#EDE6E1] bg-white shadow-lg">
          {searchable && (
            <div className="border-b border-[#EDE6E1] p-2">
              <div className="relative">
                <Search className="absolute left-2 top-2 w-4 h-4 text-[#A8989F]" />
                <input
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search…"
                  className="w-full rounded-md border border-[#EDE6E1] bg-white pl-8 pr-2 py-1.5 text-sm focus:border-[#6E3450] focus:outline-none"
                />
              </div>
            </div>
          )}
          <div className="max-h-60 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-sm text-[#7B6B72] italic">No matches.</div>
            ) : (
              filtered.map((o) => {
                const checked = selected.includes(o.value);
                return (
                  <button
                    type="button"
                    key={o.value}
                    onClick={() => toggle(o.value)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-[#FAF7F1]"
                  >
                    <span
                      className={`inline-flex h-4 w-4 items-center justify-center rounded border ${
                        checked
                          ? 'border-[#6E3450] bg-[#6E3450] text-white'
                          : 'border-[#EDE6E1] bg-white'
                      }`}
                    >
                      {checked && <Check className="h-3 w-3" />}
                    </span>
                    <span className="text-[#1F1A20]">{o.label}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ErrorBanner({
  message,
  onDismiss,
}: {
  message: string;
  onDismiss?: () => void;
}): React.ReactElement {
  return (
    <div className="flex items-start justify-between gap-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
      <span className="flex-1">{message}</span>
      {onDismiss && (
        <button onClick={onDismiss} className="text-red-700 hover:text-red-900" title="Dismiss">
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

function humanError(err: unknown): string {
  const e = err as { response?: { data?: { detail?: string; message?: string } }; message?: string };
  return e?.response?.data?.detail || e?.response?.data?.message || e?.message || 'Unknown error';
}
